import type {
  DBRecurringAgreement,
  RecurringAgreementInterval,
  RecurringAgreementLine,
} from "./db-types";

/**
 * VIB-1291: pure beregninger for løpende avtaler. Delt kontrakt med
 * `run.ts` i supabase/functions/run-recurring-invoices (VIB-1292) —
 * Deno kan ikke importere frontend-lib direkte, så beregningen dupliseres
 * der med IDENTISKE tester (jf. spec-notatet under VIB-1292).
 */

const INTERVAL_MONTHS: Record<RecurringAgreementInterval, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/**
 * Bump `currentDate` (YYYY-MM-DD) med intervallets antall måneder.
 * Månedsslutt-trygg: 31. jan + 1 mnd -> 28. eller 29. feb (klemmer til siste
 * dag i målmåneden i stedet for å rulle over til neste måned, slik
 * JavaScripts native Date-aritmetikk ville gjort).
 *
 * Parses/bygges manuelt fra Y/M/D-komponenter (ikke via lokal Date-parsing)
 * for å unngå tidssone-fallgruven som rammet VIB-1339 — next_run_date er en
 * ren DATE-kolonne uten klokkeslett/sone.
 */
export function computeNextRunDate(
  currentDate: string,
  interval: RecurringAgreementInterval,
): string {
  const [y, m, d] = currentDate.split("-").map(Number);
  const monthsToAdd = INTERVAL_MONTHS[interval];

  const totalMonths = (m - 1) + monthsToAdd;
  const targetYear = y + Math.floor(totalMonths / 12);
  const targetMonthIndex = ((totalMonths % 12) + 12) % 12; // 0-11, robust for negative edge cases

  // Dag 0 i måned (index+1) = siste dag i måned (index). UTC for å unngå
  // lokal-tidssone-forskyvning av dato-komponenter.
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonthIndex + 1, 0)).getUTCDate();
  const targetDay = Math.min(d, daysInTargetMonth);

  const mm = String(targetMonthIndex + 1).padStart(2, "0");
  const dd = String(targetDay).padStart(2, "0");
  return `${targetYear}-${mm}-${dd}`;
}

/** Sum av linjer (quantity * unit_price), eks. mva. */
export function computeLineTotal(lines: RecurringAgreementLine[] | null | undefined): number {
  if (!lines?.length) return 0;
  return lines.reduce((sum, l) => sum + (l.quantity ?? 0) * (l.unit_price ?? 0), 0);
}

/** Normaliser et beløp for et gitt intervall til et månedlig ekvivalent-beløp. */
export function normalizeToMonthly(amount: number, interval: RecurringAgreementInterval): number {
  return amount / INTERVAL_MONTHS[interval];
}

/**
 * Sum løpende inntekt: kun `active`-avtaler, hver linjesum normalisert til
 * måned uansett faktureringsintervall, så en kvartalsavtale og en
 * månedsavtale kan summeres meningsfullt. Brukes av /avtaler-oversikten.
 */
export function computeMonthlyRunningRevenue(
  agreements: ReadonlyArray<Pick<DBRecurringAgreement, "lines" | "billing_interval" | "status">>,
): number {
  return agreements
    .filter((a) => a.status === "active")
    .reduce(
      (sum, a) => sum + normalizeToMonthly(computeLineTotal(a.lines), a.billing_interval),
      0,
    );
}

/** Årlig løpende inntekt: månedsekvivalenten × 12 (kundekortets «66 000 kr/år»). */
export function computeYearlyRunningRevenue(
  agreements: ReadonlyArray<Pick<DBRecurringAgreement, "lines" | "billing_interval" | "status">>,
): number {
  return Math.round(computeMonthlyRunningRevenue(agreements) * 12);
}

/** Standard varselhorisont for utløpende avtaler (kundekortets «Utløper snart»). */
export const EXPIRING_SOON_DAYS = 60;

/**
 * Aktiv avtale med sluttdato innen `withinDays` fra `today` (YYYY-MM-DD).
 * Ren strengsammenligning på DATE-kolonnen, ingen tidssone.
 */
export function isExpiringSoon(
  agreement: Pick<DBRecurringAgreement, "status" | "end_date">,
  today: string,
  withinDays = EXPIRING_SOON_DAYS,
): boolean {
  if (agreement.status !== "active" || !agreement.end_date) return false;
  if (agreement.end_date < today) return false;
  const [y, m, d] = today.split("-").map(Number);
  const limit = new Date(Date.UTC(y, m - 1, d + withinDays)).toISOString().slice(0, 10);
  return agreement.end_date <= limit;
}
